import { sendDataLayerEvent } from "@aistihealth/web-common/src/gtm/gtm-utils"
import { emailRegex } from "@aistihealth/web-common/src/util/validation-utils"
import React, { useCallback, useMemo, useState } from "react"
import { GetStaticProps, NextPage } from "next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { Formik, Form, FormikConfig } from "formik"
import * as Yup from "yup"
import { Trans, useTranslation } from "next-i18next"
import { useRouter } from "next/router"

import { Heading } from "@aistihealth/web-common/src/components/heading/Heading"
import { Paragraph } from "@aistihealth/web-common/src/components/paragraph/Paragraph"
import { Button } from "@aistihealth/web-common/src/components/button/Button"
import { SingleColLayout } from "@aistihealth/web-common/src/components/single-col-layout/SingleColLayout"
import { FormCol } from "../components/form/FormCol"
import { FormInput } from "../components/form/FormInput"
import { FormRow } from "../components/form/FormRow"
import { HeroImage } from "../components/hero-image/HeroImage"
import { SelectOption } from "../components/inputs/select-input/SelectInput"
import { MainLayout } from "../components/layouts/main-layout/MainLayout"
import { Section } from "../components/section/Section"
import { ValidationErrorMessage } from "../components/form/ValidationErrorMessage"
import { getFooter } from "../datocms/dato-utils"
import { BasePageProps, NEXT_JS_ISR_REVALIDATE } from "../util/page"
import { defaultIfString } from "../util/typing/i18n"
import { trimObject } from "../util/user-input"
import { createRequest } from "../api/zendesk"
import { truncateWEllipsis } from "../util/string"

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const footer = await getFooter({ locale, preview })

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "contact"])),
      footer,
    },
    revalidate: NEXT_JS_ISR_REVALIDATE,
  }
}

const contactFormSchema = Yup.object({
  firstName: Yup.string()
    .required()
    .trim()
    .min(1)
    .max(70),
  lastName: Yup.string()
    .required()
    .trim()
    .min(1)
    .max(70),
  email: Yup.string()
    .trim()
    .matches(emailRegex, "forms.validation.emailInvalid")
    .max(200),
  organization: Yup.string()
    .trim()
    .min(1)
    .max(70),
  phoneNumber: Yup.string()
    .trim()
    .matches(
      /^(\+|0)([0-9]| |-)+$/,
      "forms.validation.phoneNumberShouldBeInIntlOrFinnishFormat",
    )
    .min(7)
    .max(31),
  reason: Yup.string().required(),
  message: Yup.string()
    .trim()
    .required()
    .min(5)
    .max(2000),
})

type ContactFormFields = Yup.Asserts<typeof contactFormSchema>
type ContactFormSubmitFn = FormikConfig<ContactFormFields>["onSubmit"]

const initialValues: ContactFormFields = {
  firstName: "",
  lastName: "",
  email: "",
  organization: "",
  phoneNumber: "",
  reason: "",
  message: "",
}

const formId = "contact"

const Contact: NextPage<BasePageProps> = ({ footer }) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const [loading, setLoading] = useState(false)
  const [complete, setComplete] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const reasonOptions: SelectOption[] = useMemo(
    () => [
      {
        displayName: t([`common:forms.selectPlaceholder`]),
        value: "",
      },
      ...["requestDemo", "needSupport", "interestedInPartnership", "other"].map(
        reason => ({
          displayName:
            t([`contact:form.reasonOptions.${reason}`, ""]) || reason,
          value: reason,
        }),
      ),
    ],
    [t],
  )

  const introParagraphs: string[] = defaultIfString(
    t("contact:intro.paragraphs", {
      returnObjects: true,
    }),
    [],
  )

  const handleSubmit: ContactFormSubmitFn = useCallback(
    values => {
      setLoading(true)
      setSubmitError(false)

      const {
        email,
        firstName,
        lastName,
        message,
        ...plainTextListValues
      } = trimObject(values)

      const name = `${firstName} ${lastName}`
      const plainTextListTranslations = defaultIfString(
        t("common:forms.fieldLabels", {
          returnObjects: true,
        }),
        {},
      )
      const plainTextList = Object.entries(plainTextListValues)
        .map(([key, value]) => {
          if (!value) {
            return undefined
          }

          // Translate reason
          const formattedValue =
            key === "reason"
              ? reasonOptions.find(
                  ({ value: reasonOptionValue }) => reasonOptionValue === value,
                )?.displayName ?? value
              : value

          return `* ${plainTextListTranslations[key] ?? key}: ${formattedValue}`
        })
        .filter(value => value !== undefined)
        .join("\n")

      sendDataLayerEvent({
        contactReason: plainTextListValues.reason,
        event: "contactRequest",
      })

      const body = `${plainTextList}\n\n---\n\n${message}`
      const subject = truncateWEllipsis(message, 50)

      createRequest({
        comment: {
          body,
        },
        custom_fields: [{ id: 5927122730001, value: "contact_form" }],
        requester: {
          email: email || undefined,
          locale,
          name,
        },
        subject,
      })
        .then(() => {
          setComplete(true)
          setLoading(false)
        })
        .catch(error => {
          setLoading(false)
          setSubmitError(true)

          // Sentry will catch this
          throw error
        })
    },
    [locale, reasonOptions, t],
  )

  const fieldLabels: Record<string, string> = defaultIfString(
    t("common:forms.fieldLabels", {
      returnObjects: true,
    }),
    {},
  )

  const headLine = t(["contact:hero.title", ""])

  const hero = (
    <HeroImage
      title={headLine}
      image={{
        brightness: "89%",
        originalHeight: 2000,
        originalWidth: 3000,
        src:
          "https://www.datocms-assets.com/74423/1698231298-contactus-3000.jpg",
      }}
    />
  )

  const mainContent = (
    <>
      {introParagraphs.map(text => (
        <Paragraph size="xlarge" key={text}>
          <Trans>{text}</Trans>
        </Paragraph>
      ))}
      <Heading size="xlarge">{t("contact:form.title")}</Heading>
      <Formik<ContactFormFields>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={contactFormSchema}
      >
        {({ isValid, touched, values }) => {
          const numFields = Object.keys(values).length
          const numTouched = Object.entries(touched).filter(
            ([, value]) => !!value,
          ).length
          const allTouched = numFields === numTouched

          return (
            <Form noValidate>
              <FormRow>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="firstName"
                    displayName={fieldLabels.firstName ?? "firstName"}
                    required
                  />
                </FormCol>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="lastName"
                    displayName={fieldLabels.lastName ?? "lastName"}
                    required
                  />
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="email"
                    displayName={fieldLabels.email ?? "email"}
                  />
                </FormCol>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="organization"
                    displayName={fieldLabels.organization ?? "organization"}
                  />
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="phoneNumber"
                    displayName={fieldLabels.phoneNumber ?? "phoneNumber"}
                  />
                </FormCol>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="select"
                    formId={formId}
                    name="reason"
                    displayName={fieldLabels.reason ?? "reason"}
                    required
                    options={reasonOptions}
                  />
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol xs={12}>
                  <FormInput
                    type="textarea"
                    formId={formId}
                    name="message"
                    displayName={fieldLabels.message ?? "message"}
                    required
                  />
                </FormCol>
              </FormRow>
              <FormRow align="right">
                <FormCol xs={12}>
                  <Button type="submit" variant="primary" disabled={loading}>
                    {t("forms.controls.submit")}
                  </Button>
                </FormCol>
              </FormRow>
              <FormRow align="right">
                <FormCol xs={12}>
                  {!isValid && allTouched && (
                    <ValidationErrorMessage as="div" size="medium">
                      {t("forms.validation.checkFields")}
                    </ValidationErrorMessage>
                  )}
                  {submitError && (
                    <ValidationErrorMessage as="div" size="medium">
                      <Trans
                        i18nKey="contact:form.submitError"
                        components={{
                          // eslint-disable-next-line jsx-a11y/anchor-has-content
                          a: <a />,
                        }}
                      />
                    </ValidationErrorMessage>
                  )}
                </FormCol>
              </FormRow>
            </Form>
          )
        }}
      </Formik>
    </>
  )

  const postSubmitContent = (
    <>
      <Heading size="xlarge">{t("contact:postSubmit.title")}</Heading>
      <Paragraph size="large">{t("contact:postSubmit.body")}</Paragraph>
    </>
  )

  return (
    <MainLayout
      type="legacy"
      title={t(["contact:head.title", ""])}
      shareDescription={headLine}
      heroComponent={hero}
      footer={footer}
    >
      <Section backgroundColor="white">
        <SingleColLayout
          colProps={{
            sm: 8,
            smOffset: 2,
            xs: 12,
          }}
        >
          {!complete && mainContent}
          {complete && postSubmitContent}
        </SingleColLayout>
      </Section>
    </MainLayout>
  )
}

export default Contact
